import React, { useCallback, useEffect, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ReactComponent as LinkIcon } from '@pkg/assets/images/artifacts/link.svg';
import { ButtonDeprecated, EffectCount, EffectSlideIn, MintButton, MintTile } from '@pkg/components';
import { useAppDispatch, useAppSelector, useMobile } from '@pkg/hooks';

import { ArtifactType } from '@app/client/common/structs/nft/artifacts';
import config from '@app/config';
import { getTranslationByArtifactKey } from '@app/helpers/artifacts/getTranslationByArtifactKey';
import { PathParams } from '@app/routes/types';
import { setArtifactMintPanelOpen } from '@app/store/actions/artifacts';
import { artifactCountsSelector } from '@app/store/selectors/artifacts/artifacts';
import { usersOwnedArtifacts } from '@app/store/selectors/artifacts/artifacts-metadata';
import {
    artifactMintBreakdownSelector,
    artifactMintSelector,
    userArtifactMintSelector,
} from '@app/store/selectors/artifacts/minting';
import { toggle2faArtifactMintingPopup } from '@app/store/slices';

import './DetailContent.scss';

interface DetailContentProps {
    artifactType: ArtifactType;
}

const { nftOpenseaLink } = config;

export function DetailContent({ artifactType }: DetailContentProps) {
    const { isMobile } = useMobile(); //todo is there a better way around this?
    const dispatch = useAppDispatch();

    const { t } = useTranslation(['artifacts', 'words']);
    const { [PathParams.Id]: artifactId } = useParams();

    const [userOwnsArtifact, setUserOwnsArtifact] = useState(false);

    const user = useAppSelector((state) => state.user);
    const { showArtifactSync } = useAppSelector((state) => state.featureFlags.features);
    const artifactSupplyCounts = useAppSelector(artifactCountsSelector); //todo make a selector that has everything :/
    const userArtifactMintDetails = useAppSelector(userArtifactMintSelector);
    const userArtifactOwnedItems = useAppSelector(usersOwnedArtifacts);
    const artifactMintBreakdown = useAppSelector(artifactMintBreakdownSelector);
    const artifactMintDetails = useAppSelector(artifactMintSelector);

    const totalInCollection = userArtifactOwnedItems[artifactType]?.length;
    const remainingSupply = artifactSupplyCounts[artifactType]?.remaining_supply;
    const tokenMapping = artifactMintBreakdown[artifactType]?.lootbox_token_id_mapping;
    const contractAddress = artifactMintDetails[artifactType]?.contract_address;
    const openSeaPath = `${nftOpenseaLink}/0x${contractAddress}`;

    const twoFactorEnabled = user?.userAccount?.user.two_factor_auth;

    useEffect(() => {
        const ownsTypeOfArtifact = Boolean(totalInCollection && totalInCollection > 0);
        // only Celestial Roses are checked against the id
        if (artifactType === ArtifactType.CelestialRose) {
            // but not on mobile, or the main artifact page item because we don't use the id in the route
            const useId = artifactId && !isMobile;
            setUserOwnsArtifact(
                useId ? !!userArtifactOwnedItems[artifactType]?.find((id) => id === artifactId) : ownsTypeOfArtifact,
            );
        } else {
            //everything else is based on if the user owns any
            setUserOwnsArtifact(ownsTypeOfArtifact || false);
        }
    }, [isMobile, artifactType, setUserOwnsArtifact, userArtifactOwnedItems, totalInCollection, artifactId]);

    const handleMintClick = useCallback(() => {
        dispatch(twoFactorEnabled ? setArtifactMintPanelOpen(true) : toggle2faArtifactMintingPopup(true));
    }, [dispatch, twoFactorEnabled]);

    const handleClick = useCallback(() => {
        const translationKey = userOwnsArtifact ? 'twitter' : 'twitter_uncollected';
        const params = {
            text: getTranslationByArtifactKey(t, artifactType, translationKey),
            url: window.location.href,
        };
        const shareURL = encodeURI(`https://twitter.com/intent/tweet?text=${params.text}&url=${params.url}`);
        window.open(shareURL, '__blank');
    }, [t, artifactType, userOwnsArtifact]);

    const base = `yat-artifacts__detail-content`;
    const eligibleToMint = userArtifactMintDetails[artifactType]?.eligible_for_minting;
    const disabled = !showArtifactSync || !eligibleToMint?.length;
    const mintedItemIds = userArtifactMintDetails[artifactType]?.mintedIds;
    return (
        <aside className={`${base}`}>
            <ButtonDeprecated
                classes={[`${base}__share ${base}__share--desktop`]}
                size={'large'}
                onClick={handleClick}
                icon={<LinkIcon />}
                color={'tertiary'}
            >
                {t('words:share')}
            </ButtonDeprecated>
            <div className={`${base}__scroll`}>
                <div className={`${base}__scroll__content`}>
                    <h1 className={`${base}__headline`}>
                        <EffectSlideIn delay={750}>{t(`label_${artifactType.toLowerCase()}`)}</EffectSlideIn>
                    </h1>
                    <p className={`${base}__section`}>{t('words:collection')}</p>
                    <div className={`${base}__values`}>
                        <div className={`${base}__value`}>
                            <p className={`${base}__value-title`}>
                                <EffectCount to={remainingSupply || 0} />
                            </p>
                            <p className={`${base}__value-amount`}>{t('remaining_supply')}</p>
                        </div>
                        <div className={`${base}__value`}>
                            <p className={`${base}__value-title`}>
                                <EffectCount to={totalInCollection || 0} />
                            </p>
                            <p className={`${base}__value-amount`}>{t('in_collection')}</p>
                        </div>
                    </div>
                    <ButtonDeprecated
                        classes={[`${base}__share ${base}__share--mobile`]}
                        size={'small'}
                        onClick={handleClick}
                        icon={<LinkIcon />}
                        color={'tertiary'}
                    >
                        {t('words:share')}
                    </ButtonDeprecated>

                    <p className={`${base}__section`}>{t('words:origin')}</p>
                    <p className={`${base}__paragraph`}>{t(`detail_${artifactType.toLowerCase()}`)}</p>

                    <p className={`${base}__paragraph`}>
                        {userOwnsArtifact && eligibleToMint && eligibleToMint?.length > 0 && (
                            <MintButton
                                disabled={disabled} //ha! does now
                                copy={t('mint_artifact')}
                                hoverCopy={t('mint_coming_soon')}
                                onClick={handleMintClick}
                            />
                        )}
                    </p>

                    {!isEmpty(mintedItemIds) && <p className={`${base}__section`}>{t('minted_artifacts')}</p>}
                    <div className={`${base}__tile-holder`}>
                        {mintedItemIds?.map((id) => {
                            const lootBoxId =
                                tokenMapping && Object.keys(tokenMapping).find((lootBoxId) => lootBoxId === id);
                            const tokenId = lootBoxId && tokenMapping[lootBoxId];
                            const openseaUrl = `${openSeaPath}/${tokenId}`;
                            return (
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={openseaUrl}
                                    key={id}
                                    className={`${base}__tile`}
                                >
                                    <MintTile
                                        minted
                                        artifactType={artifactType}
                                        artifactId={lootBoxId || artifactId}
                                        tokenId={tokenId}
                                    />
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>
        </aside>
    );
}
